/**
 * @file Map-marker-layer-manager.tsx
 * @module MapMarkerLayerManager
 * @description Kartes slāņu pārvaldnieks marķieru mini kartei.
 *              Izveido MVT (Mapbox Vector Tiles) slāņus no ĢIS datu servisu datiem,
 *              izmantojot Mapbox stilizāciju. Slāņi tiek grupēti LayerGroup objektā
 *              un pievienoti marķieru kartei kā fona konteksts ģeometriju rediģēšanai.
 */
import type { VectorTile } from "ol";
import Feature from "ol/Feature";
import MVT from "ol/format/MVT";
import VectorTileLayer from "ol/layer/VectorTile";
import VectorTileSource from "ol/source/VectorTile";
import { DataResponse, LineStyle, PointStyle } from "../store/Layers.repository";
import { Map } from "ol";
import { createMapboxStyle } from "../data-layers/Mapbox-style-generator";
import { stylefunction } from "ol-mapbox-style";
import spriteJson from "../shared/sprites/icons.json";
import iconspng from "../shared/sprites/icons.png";
import LayerGroup from "ol/layer/Group";
import { getMVTStyles } from "../data-layers/Mapbox-helper";

const createSource = (url: string, lines: LineStyle[], points: PointStyle[]) => {
	const source = new VectorTileSource({
		transition: 150,
		url,
		projection: "EPSG:3857",
		cacheSize: 0,
		format: new MVT({ featureClass: Feature as any }),
		wrapX: false,
		tileLoadFunction: (tile, url) => {
			const t = tile as VectorTile<Feature>;
			t.setLoader((extent, resolution, projection) => {
				fetch(url).then((res) => {
					res.arrayBuffer().then((data) => {
						const format = t.getFormat();
						let features = format.readFeatures(data, {
							extent: extent,
							featureProjection: projection,
						}) as Feature[];

						features.forEach((f) => {
							f.setProperties(
								{
									...(f as any).properties_,
									internal: {
										lines: lines,
										points: points,
									},
								},
								true
							);
						});

						t.setFeatures(features as any);
					});
				});
			});
		},
	});
	return source;
};

const createLayer = (url: string, lines: LineStyle[], points: PointStyle[]) => {
	const layer = new VectorTileLayer({
		zIndex: -1,
		renderOrder: null as unknown as any,
		declutter: true,
		renderBuffer: 36,
		useInterimTilesOnError: false,
		className: "",
		source: createSource(url, lines, points),
	});
	return layer;
};

type MapMarkerLayerManagerProps = {
	map: Map;
	data: DataResponse[];
};

export const MapMarkerLayerManager: React.FC<MapMarkerLayerManagerProps> = ({ map, data }) => {
	if (
		map
			.getLayers()
			.getArray()
			.find((l) => l instanceof LayerGroup)
	)
		return <></>;

	const labelStyles = ["pikets_box", "annotation"]; //Names of layers to show on map, must include also english version
	const layers = data.flatMap((d) => d.layers).filter((l) => labelStyles.includes(l.label_style));
	const urls = new Set(layers.map((l) => l.merged_tiles!));

	const lg = new LayerGroup({
		layers: Array.from(urls).map((url) => {
			const [lines, points] = getMVTStyles(layers, true);

			const layer = createLayer(url, lines, points);
			layer.set("id", url);
			const mapboxStyle = createMapboxStyle(lines, points, url, false);
			stylefunction(layer, mapboxStyle, "features", undefined, spriteJson, iconspng);
			return layer;
		}),
	});

	map.addLayer(lg);
	map.changed();

	return <></>;
};
